import { createSceneCapabilities } from '@components/Flows/sceneCapabilities/index.js';
import timer_gen_2 from '@devices/settings/device_settings/timer_gen_2.js';
import schedule_gen_2 from '@devices/settings/device_settings/schedule_gen_2.js';
import webhooks_gen_2 from '@devices/settings/device_settings/webhooks_gen_2.js';
import scripts_gen_2 from '@devices/settings/device_settings/scripts_gen_2.js';
import networks_gen_2_pro from '@devices/settings/device_settings/networks_gen_2_pro.js';
import virtual_components_pro_devices from '@devices/settings/device_settings/virtual_components_pro_devices.js';
import safety_gen_2_auto_recovery_under_voltage from '@devices/settings/device_settings/safety_gen_2_auto_recovery_under_voltage.js';
import app_setting_pm_gen_2 from '@devices/settings/device_settings/app_setting_pm_gen_2.js';
import share from '@devices/settings/device_settings/share.js';
import settings_gen_2 from '@devices/settings/device_settings/settings_gen_2.js';
import bluetooth_gen_2 from '@devices/settings/device_settings/bluetooth_gen_2.js';
import addon_sensors, { addon_pro_devices } from '@devices/settings/device_settings/addon_sensors';

export default {
	name: 'ShellyPro1PM',
	friendly_name: 'Pro 1 PM',
	family: 'relay',
	isPM: true,
	addons: addon_pro_devices,
	modes: {
		relay: {
			channels: 1,
			notifications: [
				'offline',
				'device_problem',
				'overpower',
				'overvoltage',
				'forgotten_lights',
				'overtemperature',
			],
			settings: [
				timer_gen_2,
				schedule_gen_2,
				webhooks_gen_2,
				addon_sensors,
				scripts_gen_2,
				networks_gen_2_pro,
				bluetooth_gen_2,
				virtual_components_pro_devices,
				safety_gen_2_auto_recovery_under_voltage,
				app_setting_pm_gen_2,
				share,
				settings_gen_2,
			],
		},
	},
	scene: {
		actions: {
			turn: {
				name: 'turn',
				description: 'Turn relay on or off',
				category: 'power',
				parameters: {
					value: {
						type: 'enum',
						required: true,
						enumValues: ['on', 'off', 'toggle'],
						description: 'Turn relay on, off, or toggle current state',
					},
					channel: {
						type: 'number',
						required: false,
						defaultValue: 0,
						range: { min: 0, max: 0 },
						description: 'Relay channel (always 0 for single relay)',
					},
				},
				validation: [
					{
						type: 'required',
						constraint: 'value',
						errorMessage: 'Turn action requires value parameter',
						severity: 'error',
					},
				],
				commandTemplate: {
					endpoint: '/rpc/Switch.Set',
					method: 'POST',
					payload: {
						id: '{{channel}}',
						on: '{{turnValue}}',
						timeout: '{{timer}}',
					},
					responseExpected: true,
					errorHandling: 'retry',
				},
				supportedValues: {
					value: ['on', 'off', 'toggle'],
				},
				timerSupport: {
					minDuration: 1,
					maxDuration: 7200,
					units: ['seconds', 'minutes'],
					precision: 'second',
				},
				uiMetadata: {
					displayName: 'Turn On/Off',
					icon: 'power',
					category: 'Control',
					order: 1,
					helpText: 'Turn the relay on, off, or toggle its current state',
				},
			},
		},
		conditions: {
			is_on: {
				name: 'is_on',
				description: 'Check if relay is currently on',
				category: 'power',
				evaluationType: 'immediate',
				comparisonOperators: ['equals', 'not_equals'],
				parameters: {
					channel: {
						type: 'number',
						required: false,
						defaultValue: 0,
						range: { min: 0, max: 0 },
						description: 'Relay channel to check',
					},
					value: {
						type: 'boolean',
						required: true,
						description: 'Expected relay state (true = on, false = off)',
					},
				},
				validation: [
					{
						type: 'required',
						constraint: 'value',
						errorMessage: 'State condition requires value parameter',
						severity: 'error',
					},
				],
				stateProperties: {
					switch_state: {
						path: 'switch:0.output',
						type: 'boolean',
						description: 'Current relay output state',
						updateFrequency: 1,
						caching: 'short',
					},
				},
				uiMetadata: {
					displayName: 'Is On',
					icon: 'toggle-on',
					category: 'State',
					order: 1,
					helpText: 'Check if the relay is currently turned on',
				},
			},
			power_consumption: {
				name: 'power_consumption',
				description: 'Check current power consumption',
				category: 'energy',
				evaluationType: 'immediate',
				comparisonOperators: [
					'greater_than',
					'less_than',
					'greater_equal',
					'less_equal',
					'in_range',
				],
				parameters: {
					threshold: {
						type: 'number',
						required: true,
						range: { min: 0, max: 3680 },
						unit: 'W',
						description: 'Power threshold in watts',
					},
					operator: {
						type: 'enum',
						required: true,
						enumValues: ['greater_than', 'less_than', 'greater_equal', 'less_equal'],
						description: 'Comparison operator',
					},
				},
				validation: [
					{
						type: 'range',
						constraint: { min: 0, max: 3680 },
						errorMessage: 'Power threshold must be between 0 and 3680 watts',
						severity: 'error',
					},
				],
				stateProperties: {
					power: {
						path: 'switch:0.apower',
						type: 'number',
						unit: 'W',
						description: 'Current active power consumption',
						updateFrequency: 5,
						caching: 'medium',
					},
				},
				thresholdSupport: {
					supportedOperators: ['greater_than', 'less_than', 'greater_equal', 'less_equal'],
					hysteresis: 5,
					debounceTime: 1000,
				},
				uiMetadata: {
					displayName: 'Power Consumption',
					icon: 'bolt',
					category: 'Energy',
					order: 2,
					helpText: 'Monitor power consumption levels',
				},
			},
		},
		triggers: {
			state_changed: {
				name: 'state_changed',
				description: 'Triggered when relay state changes',
				category: 'power',
				triggerType: 'state_change',
				parameters: {
					channel: {
						type: 'number',
						required: false,
						defaultValue: 0,
						range: { min: 0, max: 0 },
						description: 'Relay channel to monitor',
					},
					from_state: {
						type: 'enum',
						required: false,
						enumValues: ['on', 'off', 'any'],
						defaultValue: 'any',
						description: 'Previous state to trigger from',
					},
					to_state: {
						type: 'enum',
						required: false,
						enumValues: ['on', 'off', 'any'],
						defaultValue: 'any',
						description: 'New state to trigger to',
					},
				},
				validation: [
					{
						type: 'custom',
						constraint:
							'from_state !== to_state || from_state === "any" || to_state === "any"',
						errorMessage: 'From and to states cannot be the same unless one is "any"',
						severity: 'warning',
					},
				],
				eventProperties: {
					state_change: {
						eventName: 'switch.state_changed',
						dataType: 'object',
						description: 'Relay state change event',
						frequency: 'medium',
					},
				},
				filterSupport: {
					supportedFilters: ['value_filter', 'time_filter'],
					maxFilters: 3,
					filterLogic: 'AND',
				},
				uiMetadata: {
					displayName: 'State Changed',
					icon: 'exchange-alt',
					category: 'Events',
					order: 1,
					helpText: 'Trigger when relay turns on or off',
				},
			},
			power_threshold: {
				name: 'power_threshold',
				description: 'Triggered when power crosses threshold',
				category: 'energy',
				triggerType: 'value_change',
				parameters: {
					threshold: {
						type: 'number',
						required: true,
						range: { min: 0, max: 3680 },
						unit: 'W',
						description: 'Power threshold in watts',
					},
					direction: {
						type: 'enum',
						required: true,
						enumValues: ['above', 'below', 'both'],
						defaultValue: 'both',
						description: 'Threshold crossing direction',
					},
				},
				validation: [
					{
						type: 'range',
						constraint: { min: 0, max: 3680 },
						errorMessage: 'Power threshold must be between 0 and 3680 watts',
						severity: 'error',
					},
				],
				eventProperties: {
					power_change: {
						eventName: 'switch.power_changed',
						dataType: 'number',
						description: 'Power value change event',
						frequency: 'high',
					},
				},
				filterSupport: {
					supportedFilters: ['value_filter', 'duration_filter'],
					maxFilters: 2,
					filterLogic: 'AND',
				},
				uiMetadata: {
					displayName: 'Power Threshold',
					icon: 'chart-line',
					category: 'Energy',
					order: 2,
					helpText: 'Trigger when power consumption crosses a threshold',
				},
			},
		},
	},
	// Use template-based sceneCapabilities for maintainability
	sceneCapabilities: createSceneCapabilities({
		template: 'single_relay',
	}),
};
